/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AddReportPhotosDto } from '../models/AddReportPhotosDto';
import type { CreateReportDto } from '../models/CreateReportDto';
import type { ReportDetailResponse } from '../models/ReportDetailResponse';
import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';
export class ReportsService {
    constructor(public readonly httpRequest: BaseHttpRequest) {}
    /**
     * Create a report for an intervention
     * Assigned agent creates an immutable report with snapshots of the intervention, agent, client, and society. Optional notes and photos (with captions) can be attached. Transitions the intervention to DONE status.
     * @returns ReportDetailResponse Report created successfully. Intervention transitioned to DONE.
     * @throws ApiError
     */
    public reportsControllerCreate({
        interventionId,
        requestBody,
    }: {
        /**
         * Intervention unique identifier
         */
        interventionId: string,
        requestBody: CreateReportDto,
    }): CancelablePromise<ReportDetailResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/interventions/{interventionId}/report',
            path: {
                'interventionId': interventionId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Intervention not in REPORT_IN_PROGRESS state or report already exists.`,
                401: `Unauthorized.`,
                403: `Forbidden. AGENT role required and must be the assigned agent.`,
                404: `Intervention not found.`,
            },
        });
    }
    /**
     * List reports
     * Returns a paginated list of reports filtered by user role. Clients see their own reports, agents see reports they filed, society owners see reports for their societies, admins see all.
     * @returns any List of reports.
     * @throws ApiError
     */
    public reportsControllerFindAll({
        page = 1,
        limit = 20,
        sortBy,
        sortOrder = 'desc',
        clientId,
        agentId,
        agentName,
        societyId,
        createdAfter,
        createdBefore,
        hasEffraction,
        fields,
    }: {
        /**
         * Page number
         */
        page?: number,
        /**
         * Items per page (max 200)
         */
        limit?: number,
        /**
         * Field to sort by
         */
        sortBy?: string,
        /**
         * Sort order (asc/desc)
         */
        sortOrder?: 'asc' | 'desc',
        /**
         * Filter by client user ID
         */
        clientId?: string,
        /**
         * Filter by agent user ID
         */
        agentId?: string,
        /**
         * Filter by agent name (first or last name)
         */
        agentName?: string,
        /**
         * Filter by society ID
         */
        societyId?: string,
        /**
         * Filter reports created after this date (ISO 8601)
         */
        createdAfter?: string,
        /**
         * Filter reports created before this date (ISO 8601)
         */
        createdBefore?: string,
        /**
         * Filter by whether an effraction was detected (true/false)
         */
        hasEffraction?: string,
        /**
         * Comma-separated sparse fieldset of top-level fields to include. Allowed: id, interventionId, notes, createdAt, pdfStoragePath, agentSnapshot, clientSnapshot, societySnapshot, interventionSnapshot, intervention, photos. Reduces payload size on list queries.
         */
        fields?: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/reports',
            query: {
                'page': page,
                'limit': limit,
                'sortBy': sortBy,
                'sortOrder': sortOrder,
                'clientId': clientId,
                'agentId': agentId,
                'agentName': agentName,
                'societyId': societyId,
                'createdAfter': createdAfter,
                'createdBefore': createdBefore,
                'hasEffraction': hasEffraction,
                'fields': fields,
            },
            errors: {
                401: `Unauthorized.`,
            },
        });
    }
    /**
     * Get report detail
     * Returns a single report with full intervention details, snapshots, and photos. Only participants (client, agent, society owner) and admins can access.
     * @returns ReportDetailResponse Report found.
     * @throws ApiError
     */
    public reportsControllerFindOne({
        id,
    }: {
        /**
         * Report unique identifier
         */
        id: string,
    }): CancelablePromise<ReportDetailResponse> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/reports/{id}',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized.`,
                403: `Forbidden. Not a participant.`,
                404: `Report not found.`,
            },
        });
    }
    /**
     * Add photos to a report
     * Allows the assigned agent to add additional photos (with optional captions) to an existing report.
     * @returns ReportDetailResponse Photos added successfully.
     * @throws ApiError
     */
    public reportsControllerAddPhotos({
        id,
        requestBody,
    }: {
        /**
         * Report unique identifier
         */
        id: string,
        requestBody: AddReportPhotosDto,
    }): CancelablePromise<ReportDetailResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/reports/{id}/photos',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                401: `Unauthorized.`,
                403: `Forbidden. AGENT role required and must be the assigned agent.`,
                404: `Report not found.`,
            },
        });
    }
    /**
     * Get report PDF signed URL
     * Returns a signed URL to download the report PDF. The PDF is generated asynchronously on report creation and stored in a private bucket. The signed URL expires after 60 minutes.
     * @returns any Signed URL returned.
     * @throws ApiError
     */
    public reportsControllerGetPdf({
        id,
    }: {
        /**
         * Report unique identifier
         */
        id: string,
    }): CancelablePromise<{
        url?: string;
    }> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/api/reports/{id}/pdf',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized.`,
                403: `Forbidden. Not a participant.`,
                404: `Report or PDF not found.`,
            },
        });
    }
    /**
     * Regenerate report PDF
     * Triggers PDF regeneration for a report. Only admins can request this.
     * @returns any PDF regeneration initiated.
     * @throws ApiError
     */
    public reportsControllerRegeneratePdf({
        id,
    }: {
        /**
         * Report unique identifier
         */
        id: string,
    }): CancelablePromise<any> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/api/reports/{id}/regenerate-pdf',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized.`,
                403: `Forbidden. ADMIN role required.`,
                404: `Report not found.`,
            },
        });
    }
}
